import type { ActionItem } from "./actionItems";

type ActionItemStatus = "open" | "done" | "dismissed";

export const OVERLAY_KEY = "ns_action_item_overlays";

export type ActionItemOverlay = {
  assignee_id: string | null;
  assignee_name: string | null;
  status: ActionItemStatus;
  completed_at: string | null;
};

export function loadOverlays(): Record<string, Partial<ActionItemOverlay>> {
  try {
    const raw = localStorage.getItem(OVERLAY_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveOverlay(itemId: string, patch: Partial<ActionItemOverlay>) {
  try {
    const all = loadOverlays();
    all[itemId] = { ...all[itemId], ...patch };
    localStorage.setItem(OVERLAY_KEY, JSON.stringify(all));
  } catch {
    /* ignore quota/availability errors */
  }
}

export function removeOverlay(itemId: string) {
  try {
    const all = loadOverlays();
    if (!(itemId in all)) return;
    delete all[itemId];
    localStorage.setItem(OVERLAY_KEY, JSON.stringify(all));
  } catch {
    /* ignore quota/availability errors */
  }
}

export function applyOverlays(items: ActionItem[]): ActionItem[] {
  const overlays = loadOverlays();
  return items.map((item) => {
    const ov = overlays[item.id];
    if (!ov) return item;
    return {
      ...item,
      ...(ov.status !== undefined ? { status: ov.status } : {}),
      ...(ov.completed_at !== undefined ? { completed_at: ov.completed_at } : {}),
    };
  });
}

export const openItemsCount = (): number => {
  const overlays = loadOverlays();
  let cached: Array<{ id: string; status: string }> = [];
  try {
    const raw = localStorage.getItem("ns_action_items_cache");
    cached = raw ? JSON.parse(raw) : [];
  } catch {
    cached = [];
  }
  if (!Array.isArray(cached)) cached = [];
  return cached.reduce(
    (n, item) => ((overlays[item.id]?.status ?? item.status) === "open" ? n + 1 : n),
    0
  );
};

export function actionItemStatusCounts(): {
  open: number;
  done: number;
  dismissed: number;
  total: number;
} {
  const overlays = loadOverlays();
  const counts = { open: 0, done: 0, dismissed: 0, total: 0 };
  let cached: Array<{ id: string; status: string }> = [];
  try {
    const raw = localStorage.getItem("ns_action_items_cache");
    cached = raw ? JSON.parse(raw) : [];
  } catch {
    cached = [];
  }
  if (!Array.isArray(cached)) cached = [];
  for (const item of cached) {
    const status = overlays[item.id]?.status ?? item.status;
    counts.total += 1;
    if (status === "open") counts.open += 1;
    else if (status === "done") counts.done += 1;
    else if (status === "dismissed") counts.dismissed += 1;
  }
  return counts;
}

export function computeResolvedPercent(counts: {
  open: number;
  done: number;
  dismissed: number;
}): number {
  const total = counts.open + counts.done + counts.dismissed;
  if (total === 0) return 0;
  return Math.round(((counts.done + counts.dismissed) / total) * 100);
}

export function resolvedPercent(): number {
  return computeResolvedPercent(actionItemStatusCounts());
}

export type MergedActionItem = {
  id: string;
  customer_id: string;
  caller_phone: string;
  caller_name: string | null;
  text: string;
  status: ActionItemStatus;
  source_call_id: string | null;
  created_at: string;
  completed_at: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
};

type MockActionItem = Omit<MergedActionItem, "assignee_id" | "assignee_name">;

function startOfLocalToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function isWithinLast7LocalDays(isoDate: string): boolean {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return false;
  const cutoff = startOfLocalToday();
  cutoff.setDate(cutoff.getDate() - 6);
  return d >= cutoff;
}

export function mergedActionItems(): MergedActionItem[] {
  const overlays = loadOverlays();
  let cached: MockActionItem[] = [];
  try {
    const raw = localStorage.getItem("ns_action_items_cache");
    cached = raw ? JSON.parse(raw) : [];
  } catch {
    cached = [];
  }
  if (!Array.isArray(cached)) cached = [];
  return cached.map((item) => {
    const base: MergedActionItem = {
      ...item,
      assignee_id: null,
      assignee_name: null,
    };
    const ov = overlays[item.id];
    return ov ? { ...base, ...ov } : base;
  });
}

export function doneThisWeekCount(): number {
  return mergedActionItems().filter(
    (item) =>
      item.status === "done" &&
      item.completed_at != null &&
      isWithinLast7LocalDays(item.completed_at),
  ).length;
}

export function recentlyCompletedItems(limit = 6): MergedActionItem[] {
  return mergedActionItems()
    .filter((item) => item.status === "done" && item.completed_at != null)
    .sort(
      (a, b) =>
        new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime(),
    )
    .slice(0, limit);
}
